import { Fragment, useEffect, useState, memo } from 'react';
import { css } from 'twin.macro';
import { connect, useDispatch } from 'react-redux';
import { MdCancel } from 'react-icons/md';
import { toast } from 'react-toastify';
import styled from '@emotion/styled';
import { Spinner } from 'react-bootstrap';
import { eventTypeContainer, eventTypeItem } from '../../GeoLocation';
import CreateEvent from './CreateEvent';
import { LoadingSpinner } from '@/_elements/Loaders';
import { fetchWrapper } from '@/_helpers';
import dayjs from 'dayjs';
import Avatar from '@/components/Admin/reusableComponents/Avatar';
import PreviewEventModal from '@/components/Admin/Tasks/PreviewEventModal';
import { getTheTask, setUpcomingTaskList as setUpcomingTasksList } from '@/_redux/actions/ActionTasks';
import { BootstrapToolTip } from '@/_elements/ToolTip';

const EventTime = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: #fff;
`;

const EventTitle = styled.p`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;
    font-size: 14px;
`;
const eventTypeIcon = css`
    width: 48px;
    height: 48px;
    border-radius: 50%;
`;
const activeEventTypeStyle = {
    backgroundColor: '#8ee0e2',
};

const activeEventTypeRemove = css`
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    z-index: 1000;
    display: flex;
    justify-content: end;

    svg {
        fill: #ffffff !important;
        height: 30px;
        width: 30px;
    }
`;
const UpcomingEvents = memo(({ ui, getTheTask }) => {
    const [upcomingTaskList, setUpcomingTaskList] = useState(null);
    const [activeEvent, setActiveEvent] = useState({});
    const [showModal, setShowModal] = useState(null);
    const [isBusy, setIsBusy] = useState(false);
    const dispatch = useDispatch();

    const fetchUpcomingTasks = async () => {
        try {
            const response = await fetchWrapper.get(`/task/me/upcoming?date=${new Date().toISOString()}&limit=5`);
            const results = response?.results || [];
            dispatch(setUpcomingTasksList(results));
            setUpcomingTaskList(response?.results || []);
        } catch (error) {
            if (error?.name === 'AbortError') return;
            toast.error(error.message);
            setUpcomingTaskList([]);
        }
    };

    useEffect(() => {
        fetchUpcomingTasks();
    }, []);

    const handleClosed = () => {
        setShowModal(null);
        setActiveEvent({});
    };
    const handleShowEvent = async (event) => {
        try {
            setIsBusy(true);
            const id = event?._id || event?.id;
            setShowModal(id);
            const response = await getTheTask(id);
            if (response && response?._id) {
                setActiveEvent(response);
            }
        } catch (error) {
            toast.error(error?.message);
        } finally {
            setIsBusy(false);
        }
    };

    const handleShowExternalEvent = (event) => {
        setShowModal(event?._id);
        setActiveEvent(event);
    };

    return (
        <div css={eventTypeContainer}>
            {showModal && showModal === activeEvent?._id && (
                <PreviewEventModal data={activeEvent} handleClose={handleClosed} />
            )}
            <CreateEvent fetchUpcomingTasks={fetchUpcomingTasks} />
            {ui?.loading || !upcomingTaskList ? (
                <LoadingSpinner />
            ) : (
                <Fragment>
                    {!upcomingTaskList.length && (
                        <div
                            css={[
                                eventTypeItem,
                                css`
                                    background: white;
                                `,
                            ]}
                        >
                            <span>No Events</span>
                            <span>Assigned</span>
                        </div>
                    )}
                </Fragment>
            )}
            {[...(upcomingTaskList || [])]
                .sort(
                    (a, b) =>
                        new Date(a?.demandDate || a?.latestDatePointer) - new Date(a?.demandDate || b.latestDatePointer)
                )
                .map((task, index) => {
                    const eventTitle = task?.title || '';
                    const eventTypeImage = task?.eventType ? task.eventType.image : '';
                    const eventTypeName = task?.eventType ? task.eventType.name : '';
                    const taskId = task?._id || task?.id;
                    const eventMode = task?.eventMode;

                    const startDate = eventMode === 'live' ? task?.latestDatePointer : task?.demandDate;
                    const endDate = eventMode === 'live' ? task?.latestDueDatePointer : task?.demandDueDate;

                    const now = new Date();
                    const isOngoing =
                        startDate && endDate ? now >= new Date(startDate) && now <= new Date(endDate) : false;

                    return (
                        <div
                            key={taskId || index}
                            css={[eventTypeItem, isBusy && activeEventTypeStyle]}
                            onClick={(event) => {
                                event.stopPropagation();
                                if (isBusy) {
                                    return;
                                }
                                if (task?.externalEvent) {
                                    handleShowExternalEvent(task);
                                } else {
                                    handleShowEvent(task);
                                }
                            }}
                        >
                            <Fragment>
                                <EventTitle>{eventTitle}</EventTitle>
                                <Fragment>
                                    {isBusy && taskId === showModal ? (
                                        <div>
                                            <LoadingSpinner size={48} height={48} />
                                        </div>
                                    ) : (
                                        <Fragment>
                                            {eventTypeImage ? (
                                                <img src={eventTypeImage} alt={eventTypeName} css={eventTypeIcon} />
                                            ) : (
                                                <Avatar item={task?.eventType || task} />
                                            )}
                                        </Fragment>
                                    )}
                                </Fragment>
                                <EventTime>
                                    <div className="d-flex align-items-center">
                                        {isOngoing && (
                                            <BootstrapToolTip text="Ongoing Event">
                                                <Spinner animation="grow" size="sm" variant="danger" className="mr-2" />
                                            </BootstrapToolTip>
                                        )}
                                        {dayjs(task?.demandDate || task?.latestDatePointer).format('MM-DD-YYYY h:mm A')}
                                    </div>
                                </EventTime>

                                {activeEvent?._id === taskId && (
                                    <div
                                        css={activeEventTypeRemove}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setActiveEvent({});
                                        }}
                                    >
                                        <MdCancel />
                                    </div>
                                )}
                            </Fragment>
                        </div>
                    );
                })}
        </div>
    );
});

const mapStateToProps = (state) => ({
    ui: state.ui,
});

export default connect(mapStateToProps, { getTheTask })(UpcomingEvents);
